import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ResultDialogComponent } from './result-dialog/result-dialog.component';
import { ExchangeRate, ExchangeRateService } from 'src/app/features';
import { TablerIconsModule } from "angular-tabler-icons";

export interface ManageExchangeDialogData {
  date: Date;
}

@Component({
  selector: 'app-manage-exchange-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    TablerIconsModule
],
  templateUrl: './manage-exchange-dialog.component.html',
  styleUrl: './manage-exchange-dialog.component.scss',
})
export class ManageExchangeDialogComponent implements OnInit {
  exchangeForm: FormGroup;
  isCheckingDate = false;
  existingRecordId: string | null = null;
  private exchangeRatePattern = /^[0-9]+(\.[0-9]{1,5})?$/;

  constructor(
    private exchangeRateService: ExchangeRateService,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ManageExchangeDialogComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: ManageExchangeDialogData
  ) {
    this.exchangeForm = this.fb.group({
      date: [this.data.date, Validators.required],
      buy: ['', [Validators.required, Validators.pattern(this.exchangeRatePattern)]],
      sell: ['', [Validators.required, Validators.pattern(this.exchangeRatePattern)]],
      closeBuy: ['', [Validators.pattern(this.exchangeRatePattern)]],
      closeSell: ['', [Validators.pattern(this.exchangeRatePattern)]],
    });
  }

  ngOnInit(): void {
    this.exchangeForm.get('date')?.valueChanges.subscribe((date) => {
      if (date) {
        this.checkDateAndLoad(date);
      }
    });

    const initialDate = this.exchangeForm.get('date')?.value;
    if (initialDate) {
      this.checkDateAndLoad(initialDate);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.exchangeForm.valid) {
      const payload = this.exchangeForm.value;
      const isUpdate = !!this.existingRecordId;

      const dataToSave: Partial<ExchangeRate> = {
        exchangeDate: this.formatDateKey(payload.date),
        buyRate: parseFloat(payload.buy),
        sellRate: parseFloat(payload.sell),
        closingBuyRate: payload.closeBuy ? parseFloat(payload.closeBuy) : 0,
        closingSellRate: payload.closeSell ? parseFloat(payload.closeSell) : 0,
      };
      this.existingRecordId ? dataToSave.id = this.existingRecordId : null;
      this.exchangeRateService.save(dataToSave).subscribe({
        next: (response) => {
          // Cerrar el formulario principal primero
          this.dialogRef.close(payload);
          
          // Luego mostrar el resultado después de un pequeño delay
          // setTimeout(() => {
            this.dialog.open(ResultDialogComponent, {
              width: '448px',
              data: {
                success: true,
                date: this.formatDateDisplay(payload.date),
                message: isUpdate ? 'Actualizado con éxito' : 'Registrado con éxito'
              },
              disableClose: true
            });
          // }, 300);
        },
        error: (error) => {
          console.error('Error saving exchange rate:', error);
          this.dialog.open(ResultDialogComponent, {
            width: '448px',
            data: {
              success: false,
              message: isUpdate ? 'Error al actualizar' : 'Error al registrar'
            },
            disableClose: true
          });
        }
      });
    }
  }

  private formatDateDisplay(date: Date): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private checkDateAndLoad(date: Date): void {
    this.isCheckingDate = true;
    const key = this.formatDateKey(date);
    this.exchangeRateService.getByDate(key).subscribe(
      (data: {
        exchangeRateByDate: ExchangeRate | null;
      }) => {
        if (data && data.exchangeRateByDate) {
          const rate = data.exchangeRateByDate;
          this.existingRecordId = rate.id?.toString() || null;
          this.exchangeForm.patchValue({
            buy: rate.buyRate,
            sell: rate.sellRate,
            closeBuy: rate.closingBuyRate,
            closeSell: rate.closingSellRate,
          });
        } else {
          this.existingRecordId = null;
          this.exchangeForm.patchValue({
            buy: '',
            sell: '',
            closeBuy: '',
            closeSell: '',
          });
        }
        this.isCheckingDate = false;
      },
      (error) => {
        console.error('Error checking date:', error);
        this.isCheckingDate = false;
      }
    );
  }

  private formatDateKey(date: Date): string {
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
